// Adaptador USB-serie con FT232RL (módulo de 6 pines con puente de 5 V / 3.3 V, como el FTDI Basic de SparkFun).
import { definePart, PITCH } from './registry.js';

export const FTDI = {
  W: 17.78, // 0.7": el encabezado de 6 pines más un paso de margen a cada lado
  D: 35.56,
  PCB_Y: 2.6, // sobre el separador del encabezado
  PCB_T: 1.6,
};
FTDI.PCB_TOP = FTDI.PCB_Y + FTDI.PCB_T;

// Velocidades de la lista del monitor serie del IDE de Arduino (el FT232R llega de 300 baudios a 3 Mbaudios).
export const FTDI_BAUDS = [300, 1200, 2400, 4800, 9600, 19200, 38400, 57600, 74880, 115200, 230400, 250000, 500000, 1000000];

// Límites de corriente de la hoja de datos del FT232R (FT_000053): 3V3OUT da hasta 50 mA «para alimentar lógica externa»;
// con el puente en 5 V la salida es el bus USB, del que ningún dispositivo puede tomar más de 500 mA.
export const FTDI_LIMITS = { '3v3': 0.05, '5v': 0.5 };

// Orden del conector del cable FTDI (TTL-232R): negro (GND), CTS, VCC, TXD, RXD, DTR. Los nombres son los del FT232R
// (TXD sale hacia el RX de la placa y RXD recibe del TX de la placa).
const NAMES = ['GND', 'CTS', 'VCC', 'TXD', 'RXD', 'DTR'];
const LABELS = {
  GND: 'GND (negro)',
  CTS: 'CTS (control de flujo; no se usa)',
  VCC: 'VCC (5 V o 3.3 V según el puente)',
  TXD: 'TXD (salida: va al RX de la placa)',
  RXD: 'RXD (entrada: viene del TX de la placa)',
  DTR: 'DTR (verde; reinicio automático de la Pro Mini, no se simula)',
};

definePart({
  type: 'usb-serial',
  name: 'Adaptador USB-serie FTDI (FT232RL)',
  category: 'comunicacion',
  description: 'Convierte el USB de tu equipo en un puerto serie TTL: así el monitor serie habla con placas sin USB (Pro Mini, ATtiny85). También las alimenta con 5 V o con 3.3 V.',
  keywords: 'ftdi ft232rl ft232 usb serie uart ttl conversor adaptador serial pro mini attiny programador',
  insertable: true,
  props: [
    {
      key: 'level', label: 'Puente de voltaje', type: 'select', default: '5v',
      options: [{ value: '5v', label: '5 V (del USB)' }, { value: '3v3', label: '3.3 V (regulador del FT232RL)' }],
    },
    {
      key: 'baud', label: 'Velocidad del puerto', type: 'select', default: '9600', runtime: true,
      options: FTDI_BAUDS.map((v) => ({ value: String(v), label: v + ' baudios' })),
    },
    { key: 'puerto', label: 'Terminal del puerto', type: 'terminal', runtime: true },
  ],
  pins: NAMES.map((name, i) => ({
    name,
    label: LABELS[name],
    kind: name === 'GND' ? 'gnd' : name === 'VCC' ? 'power' : 'signal',
    x: (i - 2.5) * PITCH,
    y: 0,
    z: 0,
    insert: true,
  })),
  footprint: { w: FTDI.W, d: FTDI.D, h: 10, z: -FTDI.D / 2 + 1.5 },
  help: 'El cable USB va al equipo (siempre conectado). Con una placa sin USB (Pro Mini, ATtiny85) el monitor serie habla por este adaptador: lo que la placa manda por su TX aparece en el monitor y lo que escribes ahí sale por TXD. ' +
    'Con una placa con USB (Uno, Nano, Mega) el monitor sigue siendo el de la placa y el adaptador es otro puerto, con su propia terminal aquí abajo. ' +
    'Conecta TXD al pin RX de la placa y RXD a su TX (cruzados), y GND con GND. Elige la velocidad que usa el programa (Serial.begin), como en el monitor del IDE: ' +
    'con otra velocidad llegan caracteres sin sentido. Funciona con Serial (la USART) y con SoftwareSerial, y a 3.3 V o 5 V. ' +
    'VCC alimenta la placa: 500 mA como máximo con el puente en 5 V y 50 mA en 3.3 V (hoja de datos del FT232R). ' +
    'El control de flujo (CTS) y el reinicio automático (DTR) no se simulan.',
});
